/**
 * Who may grant an AI-over-MCP permission to an access group (owner Q9; backend `apiv2/views/edit/access_group.py`
 * `_entitlement_refused`, `demo/lib/agroups/tenant_grants.py`). The client-MCP permissions
 * (`users/client_mcp_permissions.py`: the 52, `ai_edit.manage` and `analytics.mcp_export` among them) are entitlements
 * granted by hand: a tenant admin grants one -- adds it, turns its "All" on, adds a tag -- only while holding it
 * explicitly on the operator the group is for (a licensee's group: that licensee; a parent-level or standalone group:
 * the operator), on all tags to grant all tags and on the tags added. Full access does not hold it. Removing or
 * narrowing is always allowed. The refusal: 403 `explicit_grant_required` (add_permission, aps), or a
 * `batch_permissions` `skipped` entry with that reason.
 *
 * The backend decides every call. The page reads what it can from the session's permission cache (`my_permissions`)
 * so that "Grant All" and "All Tags On" leave out what would be refused, and a single grant the granter certainly
 * cannot make is disabled with the reason. A franchise parent's staff hold one group per licensee: their cache merges
 * every group and `per_licensee` holds the licensees' groups only, so a grant held through another licensee's group
 * leaves it open whether a parent-level group grants it too ({@link UNKNOWN}): the bulk actions leave it out, a single
 * grant is offered and the backend's answer is shown in words.
 */
import { MCP_PERMISSIONS } from './mcpPermissions';

export const YES = 'yes';
export const NO = 'no';
export const UNKNOWN = 'unknown';

/** AI editing over MCP (users 0145) predates the client-MCP catalogue: explicit-only too, but not an entitlement. */
const NOT_ENTITLEMENTS = new Set(['notification.mcp_read', 'notification.mcp_write']);

/** Is `scope.action` one of the client-MCP entitlements (backend `CLIENT_MCP_PERMISSION_CODENAMES`)? */
export const isMcpEntitlement = (scope, action) => {
    const codename = `${scope}.${action}`;
    return Object.prototype.hasOwnProperty.call(MCP_PERMISSIONS, codename) && !NOT_ENTITLEMENTS.has(codename);
};

const ALL = 'all';

/** One permission set's explicit grant of `scope.action`: ALL, the tag ids it names, or null (not held). */
const reachIn = (set, scope, action) => {
    if (!set || typeof set !== 'object') return null;
    if (set.scoped_all_tags?.[scope]?.[action]) return ALL;
    const ids = set.perms?.[scope]?.[action];
    return Array.isArray(ids) && ids.length > 0 ? ids.map(Number) : null;
};

const result = (held, allTags, tags) => ({ held, allTags, tags });

/**
 * The granter's explicit grant of `scope.action` on the operator a group is for, as far as `permissions` (the session's
 * `user.permissions`) tells: `held` and `allTags` are {@link YES}, {@link NO} or {@link UNKNOWN}; `tags` is the tag ids
 * the grant may reach (an upper bound), or null for every tag.
 *
 * `franchiseParent`: the session belongs to a franchise parent's staff (`isFranchise`); `licenseeId`: the group's
 * licensee (null: a parent-level group).
 */
export const granterGrant = (permissions, scope, action, { franchiseParent = false, licenseeId = null } = {}) => {
    if (!permissions || typeof permissions !== 'object') return result(UNKNOWN, UNKNOWN, null);
    const merged = reachIn(permissions, scope, action);
    if (!merged) return result(NO, NO, []);
    const tags = merged === ALL ? null : merged;
    if (!franchiseParent) return result(YES, merged === ALL ? YES : NO, tags);

    const perLicensee = permissions.per_licensee;
    if (!perLicensee || typeof perLicensee !== 'object') return result(UNKNOWN, merged === ALL ? UNKNOWN : NO, tags);
    const own = licenseeId === null || licenseeId === undefined ? null : reachIn(perLicensee[String(licenseeId)], scope, action);
    const licenseeReaches = Object.values(perLicensee).map((set) => reachIn(set, scope, action));
    // Not held through any licensee's group: the merged grant is a parent-level group's, which counts everywhere.
    const parentLevelOnly = licenseeReaches.every((reach) => !reach);
    const held = own || parentLevelOnly ? YES : UNKNOWN;
    let allTags;
    if (merged !== ALL) allTags = NO;
    else if (own === ALL || !licenseeReaches.includes(ALL)) allTags = YES;
    else allTags = UNKNOWN;
    return result(held, allTags, allTags === NO ? tags : null);
};

const nameOf = (codename) => MCP_PERMISSIONS[codename] || codename;

const quoted = (codenames) => {
    const names = codenames.map((codename) => `“${nameOf(codename)}”`);
    if (names.length <= 5) return names.join(', ');
    return `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
};

/** What the granter needs, per kind of grant: `add` (the permission), `all_tags` (its "All"), `tags` (more tags). */
const NEEDS = {
    add: { one: 'it explicitly (not through Full access) can grant it', many: 'them explicitly (not through Full access) can grant them' },
    all_tags: { one: 'it explicitly on all tags can grant it on all tags', many: 'them explicitly on all tags can grant them on all tags' },
};

/**
 * A refused single grant in words: `body` is the response body, `kind` add | all_tags | tags. A refusal other than
 * `explicit_grant_required` reads as the backend's message, else `fallback`.
 */
export const refusalText = (body, scope, action, kind = 'add', fallback = 'Error. Please try again in a few minutes') => {
    if (body?.code === 'explicit_grant_required') {
        const name = `“${nameOf(`${scope}.${action}`)}”`;
        if (kind === 'all_tags') return `Only someone who holds ${name} explicitly on all tags can grant it on all tags.`;
        if (kind === 'tags') return `Only someone who holds ${name} explicitly on those tags can grant it on them.`;
        return `Only someone who holds ${name} explicitly (in an access group of their own, not through Full access) can grant it.`;
    }
    return body?.errors?.[0]?.message || body?.message || fallback;
};

const groupLine = (label, entries, op) => {
    const codenames = entries.filter((e) => (op === 'all_tags') === (e.op === 'set_all_tags')).map((e) => e.codename);
    if (codenames.length === 0) return null;
    const needs = NEEDS[op][codenames.length === 1 ? 'one' : 'many'];
    return `${label}: ${quoted(codenames)}. Only someone who holds ${needs}.`;
};

/**
 * The notice after a permission batch: `skipped` is the response's `skipped` (the backend refused those actions),
 * `leftOut` the actions the page did not send ({op, codename}); null when there is nothing to say.
 * `{variant, lines}`: one line per kind, the explicit-grant refusals by permission name.
 */
export const skippedNotice = (skipped, leftOut) => {
    const refused = (skipped || []).filter((s) => s.reason === 'explicit_grant_required');
    const other = (skipped || []).filter((s) => s.reason !== 'explicit_grant_required');
    const lines = [
        groupLine('Not granted', refused, 'add'),
        groupLine('Not granted on all tags', refused, 'all_tags'),
        groupLine('Left out', leftOut || [], 'add'),
        groupLine('Left out of all tags', leftOut || [], 'all_tags'),
    ].filter(Boolean);
    if (other.length > 0) {
        const detail = other.slice(0, 5).map((s) => s.codename).join(', ');
        const more = other.length > 5 ? ` and ${other.length - 5} more` : '';
        lines.push(`${other.length} ${lines.length > 0 ? 'other ' : ''}action(s) were not applied: ${detail}${more}`);
    }
    if (lines.length === 0) return null;
    return { variant: (skipped || []).length > 0 ? 'warning' : 'info', lines };
};
